import React from 'react';

/**
 * Route-level code splitting.
 *
 * lazyPage(key, loader) wraps React.lazy and remembers the loader, so the sidebar
 * and the command palette can warm a page's chunk before the click
 * (prefetchRoute('/sales/leads') on hover). A failed chunk load (new deploy while a
 * tab was open) reloads the page once instead of showing a blank screen.
 */
const LOADERS = {};
const LOADED = new Set();

function withRetry(key, loader) {
  return () => loader().then((m) => { LOADED.add(key); return m; }).catch((err) => {
    const flag = `nexus_chunk_reload_${key}`;
    try {
      if (!sessionStorage.getItem(flag)) {
        sessionStorage.setItem(flag, '1');
        window.location.reload();
        return new Promise(() => {});
      }
    } catch { /* storage blocked: fall through */ }
    throw err;
  });
}

export function lazyPage(key, loader) {
  const load = withRetry(key, loader);
  LOADERS[key] = load;
  return React.lazy(load);
}

// Path prefix → page module key (longest prefix wins).
const ROUTE_KEYS = [
  ['/sales/leads/', './pages/sales/LeadDetail'],
  ['/sales/leads', './pages/sales/LeadsList'],
  ['/sales/tasks', './pages/Tasks'],
  ['/sales/outreach', './pages/hubs/OutreachHub'],
  ['/sales/reports', './pages/hubs/ReportsHub'],
  ['/sales/data', './pages/hubs/DataHub'],
  ['/sales/pipeline', './pages/sales/PipelineKanban'],
  ['/sales/call-cadence', './pages/sales/CallCadence'],
  ['/sales/hiring-intel', './pages/sales/HiringIntel'],
  ['/sales', './pages/sales/SalesDashboard'],
  ['/recruitment/jobs', './pages/recruitment/JobsList'],
  ['/recruitment/candidates/', './pages/recruitment/CandidateDetail'],
  ['/recruitment/candidates', './pages/recruitment/CandidatesList'],
  ['/recruitment/pipeline', './pages/recruitment/Pipeline'],
  ['/recruitment/interviews', './pages/recruitment/Interviews'],
  ['/recruitment/tasks', './pages/Tasks'],
  ['/recruitment/resume-hub', './pages/recruitment/ResumeHub'],
  ['/recruitment', './pages/recruitment/RecruitmentDashboard'],
  ['/timesheet/approvals', './pages/timesheet/TimesheetApprovals'],
  ['/timesheet', './pages/timesheet/Timesheet'],
  ['/settings', './pages/Settings'],
  ['/ceo', './pages/CEODashboard'],
  ['/audit-log', './pages/AuditLog'],
  ['/expenses', './pages/expenses/ExpenseTracker'],
];

export function prefetchRoute(path) {
  if (!path) return;
  const clean = String(path).split('?')[0];
  const hit = ROUTE_KEYS.find(([prefix]) => (prefix.endsWith('/')
    ? clean.startsWith(prefix)                                  // detail pages: '/sales/leads/<id>'
    : clean === prefix || clean.startsWith(prefix + '/')));
  const key = hit && hit[1];
  if (!key || LOADED.has(key) || !LOADERS[key]) return;
  // Respect data-saver and very slow connections: no speculative downloads there.
  const c = navigator.connection;
  if (c && (c.saveData || /(^|-)2g$/.test(c.effectiveType || ''))) return;
  LOADERS[key]().catch(() => {});
}

/** Page-shaped placeholder shown while a page chunk downloads. */
export function PageSkeleton({ bare = false }) {
  return (
    <div className={`page-skeleton${bare ? ' page-skeleton-bare' : ''}`} role="status" aria-label="Loading page">
      <div className="skeleton" style={{ width: 220, height: 28, borderRadius: 8 }} />
      <div className="skeleton" style={{ width: 340, height: 14, borderRadius: 6, marginTop: 10 }} />
      <div className="page-skeleton-grid">
        {[0, 1, 2, 3].map(i => <div key={i} className="skeleton" style={{ height: 96, borderRadius: 12 }} />)}
      </div>
      <div className="skeleton" style={{ height: 320, borderRadius: 12, marginTop: 16 }} />
    </div>
  );
}
